import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { StatusPill } from './StatusPills';
import { Progress } from './ui/progress';
import { Loader2 } from 'lucide-react';
import { documentsAnalysisApi } from '../lib/api';
import { getPrivateKey } from '../lib/crypto';
import { toast } from 'sonner';

const STATUS_LABEL = {
  not_started: { label: 'Not analysed yet', tone: 'neutral' },
  queued: { label: 'Queued', tone: 'held' },
  analysing: { label: 'Analysing', tone: 'held' },
  complete: { label: 'Analysed', tone: 'done' },
  partial: { label: 'Partly analysed', tone: 'needs' },
  failed: { label: 'Could not analyse', tone: 'breach' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

const PROCESS_LABEL = {
  segmentation: 'Segmentation',
  record_matching: 'Record matching',
  text_extraction: 'Text extraction',
  radar: 'Copy comparison',
};

const PIPELINE_STEPS = [
  {
    title: 'Split it into individual records',
    body: 'A reply often contains many documents. We separate them.',
  },
  {
    title: 'Group copies of the same record',
    body: 'If another organisation sent the same review or letter, we link them.',
  },
  {
    title: 'Pull out names, places and dates',
    body: 'Blacked-out parts stay marked. Nothing is guessed.',
  },
  {
    title: 'Compare copies from different organisations',
    body: 'Recovers text one organisation blacked out but another did not, and flags redactions you could challenge.',
  },
];

/**
 * Dialog used for both single-doc Analyse and bulk Analyse all.
 *
 * Props:
 *   open, onOpenChange
 *   doc?             — single document; if omitted, treat as bulk
 *   onDone?          — called after successful queue (parent reloads docs)
 *   organisationId?  — bulk mode only: scope "Analyse all" to one organisation
 *
 * Whichever mode: closing this dialog does not stop the analysis — it keeps
 * running in the background. A summary email follows once the whole batch is
 * done, whether or not you stayed to watch it (see analysis_worker.py).
 */
export const AnalyseDocumentDialog = ({ open, onOpenChange, doc, onDone, totalUnanalysed = 0, organisationId }) => {
  const isBulk = !doc;
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [log, setLog] = useState(null);
  const [pollTick, setPollTick] = useState(0);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setSubmitted(false);
      setLog(null);
      setPollTick(0);
      setBatchId(null);
      setBatchStatus(null);
    }
  }, [open, doc?.id]);

  // Poll the analysis log every 3s after submission for single-doc mode.
  useEffect(() => {
    if (!open || !submitted || isBulk || !doc?.id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await documentsAnalysisApi.log(doc.id);
        if (!cancelled) setLog(res.data);
      } catch { /* ignore polling errors */ }
    };
    tick();
    const id = setInterval(() => setPollTick((t) => t + 1), 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, submitted, isBulk, doc?.id, pollTick]);

  // Poll the batch's overall progress every 4s after submission for bulk mode.
  useEffect(() => {
    if (!open || !submitted || !isBulk || !batchId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await documentsAnalysisApi.batchStatus(batchId);
        if (!cancelled) setBatchStatus(res.data);
      } catch { /* ignore polling errors */ }
    };
    tick();
    const id = setInterval(() => setPollTick((t) => t + 1), 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [open, submitted, isBulk, batchId, pollTick]);

  const handleStart = async () => {
    const privateKey = getPrivateKey();
    if (!privateKey) {
      toast.error('Your encryption key is not unlocked. Sign in again.');
      return;
    }
    setSubmitting(true);
    try {
      if (isBulk) {
        const res = await documentsAnalysisApi.analyseAllUnanalysed(privateKey, organisationId);
        const n = res.data?.queued_count ?? 0;
        setBatchId(res.data?.batch_id || null);
        toast.success(n === 0
          ? 'Nothing new to analyse.'
          : `${n} document${n === 1 ? '' : 's'} queued.`);
      } else {
        const res = await documentsAnalysisApi.analyse(doc.id, privateKey);
        setBatchId(res.data?.batch_id || null);
        toast.success('Document queued.');
      }
      setSubmitted(true);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'We could not start analysing this document. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const status = log?.analysis_status || doc?.analysis_status || 'not_started';
  // Analysed before (fully, partly, or it stopped): a run goes through the
  // whole document again, updating its earlier records in place
  // (services/reanalysis.py), so say so rather than "finish".
  const isRerun = !isBulk && ['complete', 'partial', 'failed', 'cancelled'].includes(doc?.analysis_status);
  const meta = STATUS_LABEL[status] || STATUS_LABEL.not_started;
  const processesRun = log?.processes_run || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="analyse-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
            {isBulk ? 'Analyse your documents' : isRerun ? 'Analyse this document again' : 'Analyse this document'}
          </DialogTitle>
          <DialogDescription className="text-ink-55">
            {isBulk
              ? `${totalUnanalysed} waiting. This is what happens to each one.`
              : (doc?.original_filename || 'This is what happens next.')}
          </DialogDescription>
        </DialogHeader>

        {!submitted && (
          <div className="space-y-4">
            {isRerun && (
              <p className="text-sm text-ink-70" data-testid="analyse-rerun-note">
                {doc?.analysis_stale ? "We've improved how documents are analysed since this one last ran. " : ''}
                This goes through the whole document again. Records it found before are updated with
                anything new, and pages it missed last time get records of their own.
              </p>
            )}
            <ol className="space-y-2.5" data-testid="analyse-pipeline-steps">
              {PIPELINE_STEPS.map((s, i) => (
                <li key={i} className="flex items-start gap-3 rounded-lg bg-ground px-4 py-3">
                  <span className="pd-mono text-ink-55 pt-1 w-5 shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium text-ink leading-snug">
                      {s.title}
                    </p>
                    <p className="text-sm text-ink-55 mt-0.5 leading-snug">
                      {s.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>

            <p className="text-sm text-ink-55 leading-relaxed pt-3 border-t border-rule">
              Your key decrypts files in memory only, then is discarded. Anything
              pulled out is encrypted again before it is saved. This takes a minute
              or two per document.
            </p>
            <p className="text-sm text-ink-55 leading-relaxed">
              You can close this window — analysis carries on in the background either
              way. Stay here and you'll watch it finish; close it and we'll email you
              once it's done instead.
            </p>
          </div>
        )}

        {submitted && (
          <div className="space-y-4" data-testid="analyse-progress">
            {isBulk ? (
              <div className="space-y-3">
                <div className="rounded-lg bg-brand-wash p-4 text-sm">
                  <p className="font-medium text-brand-ink">
                    {!batchId ? 'Nothing new to analyse' : batchStatus?.all_terminal ? 'Done' : 'Analysing'}
                  </p>
                  <p className="text-brand-ink/80 mt-0.5">
                    {!batchId
                      ? 'Everything here has already been analysed.'
                      : batchStatus?.all_terminal
                        ? `${batchStatus.complete} of ${batchStatus.total} analysed${batchStatus.failed ? `, ${batchStatus.failed} could not be analysed` : ''}.`
                        : "This runs in the background. Stay here and you'll see it finish; close this and we'll email you a summary instead."}
                  </p>
                </div>
                {batchStatus && !batchStatus.all_terminal && (
                  <Progress
                    value={batchStatus.total ? Math.round(((batchStatus.complete + batchStatus.failed) / batchStatus.total) * 100) : 0}
                    className="h-2"
                    data-testid="analyse-batch-progress"
                  />
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink-55">Status</span>
                  <StatusPill tone={meta.tone} size="sm" testId="analyse-status-badge">
                    {meta.label}
                  </StatusPill>
                </div>

                <Progress
                  value={
                    status === 'complete' ? 100
                    : status === 'analysing' ? 60
                    : status === 'queued' ? 20
                    : 0
                  }
                  className="h-2"
                />

                {log?.analysis_stage && status === 'analysing' && (
                  <p className="text-sm text-ink-55" data-testid="analyse-stage">
                    <Loader2 className="w-3 h-3 mr-1.5 inline animate-spin" aria-hidden="true" />
                    {log.analysis_stage}
                  </p>
                )}

                {log?.analysis_error && status === 'failed' && (
                  <p className="text-sm text-breach-ink" data-testid="analyse-error">
                    {log.analysis_error}
                  </p>
                )}

                {log?.analysis_summary && status === 'complete' && (
                  <div className="rounded-lg bg-brand-wash p-3 text-sm space-y-1">
                    <p className="font-medium text-brand-ink">What we found</p>
                    <p className="text-brand-ink/80">
                      {log.analysis_summary.records_created ?? 0} records from {log.analysis_summary.total_pages ?? 0} pages
                      ({log.analysis_summary.total_segments ?? 0} segments across {log.analysis_summary.total_chunks ?? 0} chunks).
                    </p>
                  </div>
                )}

                <div className="space-y-1.5 text-sm">
                  <p className="font-medium text-ink">Steps</p>
                  {Object.keys(PROCESS_LABEL).map((pid) => {
                    const run = processesRun.find((r) => r.process_id === pid);
                    return (
                      <div key={pid} className="flex items-center justify-between text-sm">
                        <span className="text-ink-55">{PROCESS_LABEL[pid]}</span>
                        {run ? (
                          <span className="pd-mono text-ink-70">
                            v{run.prompt_version} · {run.confidence != null ? `${Math.round(run.confidence * 100)}%` : '—'}
                          </span>
                        ) : (
                          <span className="text-ink-55">waiting</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          {!submitted ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleStart}
                disabled={submitting}
                data-testid="analyse-start-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                {isBulk ? 'Analyse all documents' : isRerun ? 'Analyse again' : 'Analyse this document'}
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} data-testid="analyse-close-btn">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnalyseDocumentDialog;
